import type { EnhancedAppointment } from '@bb/domain/dist/types';
import { Checkbox, Flex, Icon, Text } from '@bb/elements-web';
import { Button } from 'antd';
import React from 'react';
import styled from 'styled-components';
import {
  formatDateStr,
  formatDateStrWithYearShort,
  formatTimeStr,
} from 'utils';

const SPECIAL_APPOINTMENT_TYPES = new Set([
  'Buyer Visit',
  'Appraisal',
  'Home Inspection',
  'Photographer',
  'Pest Control',
]);

interface ActionColumnRenderProps {
  appointment: EnhancedAppointment;
  selectedRowIds: string[];
  onFlagAppointment: ({
    appointmentId,
    flagged,
  }: {
    appointmentId: string;
    flagged: boolean;
  }) => void;
  onToggleSelectedRow?: (appointment: EnhancedAppointment) => void;
}

export const ActionColumnRender = React.memo<ActionColumnRenderProps>(
  ({ appointment, selectedRowIds, onFlagAppointment, onToggleSelectedRow }) => (
    <Flex vertical align="center">
      <StyledIconButton
        data-cy="toggle-flag-appointment-button"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          event.stopPropagation();
          onFlagAppointment({
            appointmentId: appointment?.id,
            flagged: !appointment?.flagged,
          });
        }}
      >
        <Icon
          icon="warningOutlined"
          size={20}
          color={appointment?.flagged ? 'danger' : 'default'}
        />
      </StyledIconButton>
      <Checkbox
        data-cy={`service-multiple-checkbox`}
        checked={selectedRowIds.includes(appointment?.id || '')}
        onChange={() => onToggleSelectedRow?.(appointment)}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          event.stopPropagation()
        }
      />
    </Flex>
  ),
);

export const AddressColumnRender = React.memo<{
  listing: EnhancedAppointment['listing'];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onFilter: (filter: string) => void;
}>(({ listing, onFilter }) => (
  <Flex align="center" justify="space-between">
    <Flex align="center" justify="flex-start">
      <UsersIconContainer className="show-on-viewing">
        <Icon icon="usersFilled" color="green" size={16} />
      </UsersIconContainer>
      <Flex vertical>
        <Text thickness="semiBold">{listing?.line1}</Text>
        <Text>{listing?.line2}</Text>
      </Flex>
    </Flex>
    <Button
      className="show-on-hover-button"
      data-cy="filter-by-address-button"
      size="small"
      type="primary"
      shape="circle"
      icon={<Icon icon="searchOutlined" size={14} color="white" />}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onFilter(`listing:${listing?.id}`);
      }}
    />
  </Flex>
));

export const DateColumnRender = React.memo<{
  appointment: EnhancedAppointment;
}>(({ appointment }) => (
  <Flex vertical>
    <Text>{formatDateStr(appointment.time, appointment.listing.timezone)}</Text>
    <Text>
      {formatTimeStr(appointment.time, appointment.listing.timezone)} to{' '}
      {formatTimeStr(appointment.timeEnd, appointment.listing.timezone)}
    </Text>
  </Flex>
));

export const AgentColumnRender = React.memo<{
  appointment: EnhancedAppointment;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onFilter: (filter: string) => void;
}>(({ appointment, onFilter }) => (
  <Flex align="center" justify="space-between">
    <Flex vertical>
      <StyledAgentText>{appointment.coop.name}</StyledAgentText>
      <Text thickness="semiBold">{appointment.coop.org.name}</Text>
      <Text
        size="small"
        color={
          SPECIAL_APPOINTMENT_TYPES.has(appointment.type) ? 'danger' : 'default'
        }
      >
        {appointment.type}
      </Text>
    </Flex>
    <Button
      className="show-on-hover-button"
      data-cy="filter-by-agent-button"
      size="small"
      type="primary"
      shape="circle"
      icon={<Icon icon="searchOutlined" size={14} color="white" />}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onFilter(appointment.coop.name);
      }}
    />
  </Flex>
));

export const StatusColumnRender = React.memo<{
  appointment: EnhancedAppointment;
}>(({ appointment }) => (
  <Flex vertical>
    <StyledStatusText mustCall={appointment.legacyStatus.mustCall}>
      {appointment.legacyStatus.note}
    </StyledStatusText>
    {!appointment.legacyStatus.mustCall && appointment.mustCall && (
      <StyledStatusText mustCall>
        MUST CALL LISTING CONTACT TO CONFIRM
      </StyledStatusText>
    )}
    <Text>
      {`${formatDateStrWithYearShort(
        appointment.legacyStatus.time,
        appointment.listing.timezone,
      )} ${formatTimeStr(
        appointment.legacyStatus.time,
        appointment.listing.timezone,
      )}`}
    </Text>

    {['new', 'attempted', 'time_change'].includes(appointment.status) &&
      appointment.multiConf && (
        <div>
          <Icon icon="warningOutlined" size={14} color="inherit" />
          <Text color="grey">
            MULTIPLE APPROVALS (
            {
              appointment.multiConf.filter(
                contact => contact.status !== 'confirm',
              ).length
            }{' '}
            of {appointment.multiConf.length} Remaining)
          </Text>
        </div>
      )}
    {appointment.listingAgentConfirm &&
    appointment.listingAgentConfirm.enabled ? (
      <Text thickness="bold" color="danger">
        LA to Confirm
      </Text>
    ) : null}
  </Flex>
));

const UsersIconContainer = styled.span`
  &&& {
    display: inline-flex;
    align-items: center;
    padding-right: 2px;
  }
`;

const StyledAgentText = styled(Text)`
  &&& {
    word-break: keep-all;
  }
`;

const StyledIconButton = styled.span`
  &&& {
    cursor: pointer;
    padding: 4px;
    &:hover {
      opacity: 0.6;
    }
  }
`;

const StyledStatusText = styled.div<{ mustCall?: boolean }>`
  color: ${props => (props.mustCall ? 'red' : 'inherit')};
  font-weight: ${props => (props.mustCall ? 'bold' : 'normal')};
`;
